import { useEffect, useLayoutEffect, useRef } from 'react';
import type { RefObject } from 'react';
import type { MapRef } from 'react-map-gl/maplibre';
import type maplibregl from 'maplibre-gl';
import type { FeatureCollection } from 'geojson';

import type { Client } from '@/clients/types';
import type { FeatureOverlayState } from '@/components/map/utils/mapLayers';
import { addOrbitLayer, ORBIT_LAYER_IDS } from '@/components/map/utils/mapLayerOrder';
import {
    AIR_VALVE_ICON_URL,
    AIR_VALVE_MARKER_HEIGHT,
    AIR_VALVE_MARKER_WIDTH,
    HIGH_RISK_AIR_VALVE_ICON_URL,
} from '@/components/map/utils/airValveMarkers';
import { fetchAirValvesForBounds } from '@/api/airValvesApi';
import { attachViewportLoader, EMPTY_FEATURE_COLLECTION, setGeoJsonSourceData } from '@/utils/mapUtils';
import { getMarkerPixelRatio, loadSvgAsImageData } from '@/utils/svgImage';

// Normal air valves cluster in their own source; high-risk valves in a separate source so
// the two categories cluster independently (MapLibre clusters every point in a source by
// proximity regardless of properties, so separate clusters require separate sources).
export const AIR_VALVE_SOURCE_ID = 'orbit-air-valves-source';
export const AIR_VALVE_HIGH_RISK_SOURCE_ID = 'orbit-air-valves-high-risk-source';

// Street-level detail, aligned with POIs so air valves appear at the same zoom band.
const AIR_VALVE_MIN_ZOOM = 14;

// Clustering config mirrors the entity source (useEntitiesLayer) so both products cluster identically.
const AIR_VALVE_CLUSTER_MAX_ZOOM = 16;
const AIR_VALVE_CLUSTER_RADIUS = 50;

// SAVI-themed cluster bubble colours, matching the marker artwork: purple normal valves
// (air-valve.svg #8E4EC6), red high-risk valves (air-valve-high-risk.svg #FF3131).
const AIR_VALVE_CLUSTER_COLOR = '#8E4EC6';
const HIGH_RISK_AIR_VALVE_CLUSTER_COLOR = '#FF3131';

const AIR_VALVE_IMAGE_ID = 'air-valve';
const HIGH_RISK_AIR_VALVE_IMAGE_ID = 'air-valve-high-risk';

/** Features that are not high-risk valves (the normal-valve source / clusters). */
function selectNormalValves(data: FeatureCollection): FeatureCollection {
    return {
        type: 'FeatureCollection',
        features: data.features.filter((f) => f.properties?.highRisk !== true),
    };
}

/** Features that are high-risk valves (the high-risk source / clusters). */
function selectHighRiskValves(data: FeatureCollection): FeatureCollection {
    return {
        type: 'FeatureCollection',
        features: data.features.filter((f) => f.properties?.highRisk === true),
    };
}

/**
 * Registers the two air-valve marker images (purple normal, red high-risk) with the map.
 * The artwork is fetched from the static /public SVG files and rasterised to the marker
 * size. Uses the same double-`hasImage` guard pattern as the POI markers so concurrent
 * style reloads never double-register, and swallows failures as progressive enhancement.
 */
export async function registerAirValveImages(mlMap: maplibregl.Map): Promise<void> {
    const images: Array<{ id: string; url: string }> = [
        { id: AIR_VALVE_IMAGE_ID, url: AIR_VALVE_ICON_URL },
        { id: HIGH_RISK_AIR_VALVE_IMAGE_ID, url: HIGH_RISK_AIR_VALVE_ICON_URL },
    ];
    const pixelRatio = getMarkerPixelRatio();
    for (const { id, url } of images) {
        if (mlMap.hasImage(id)) continue;
        try {
            const response = await fetch(url);
            if (!response.ok) continue;
            const svg = await response.text();
            const imageData = await loadSvgAsImageData(
                svg,
                AIR_VALVE_MARKER_WIDTH,
                AIR_VALVE_MARKER_HEIGHT,
                pixelRatio,
            );
            if (!mlMap.hasImage(id)) mlMap.addImage(id, imageData, { pixelRatio });
        } catch {
            // Progressive enhancement — a missing marker image falls back to the default icon.
        }
    }
}

/** Registers a clustered GeoJSON source plus its cluster-circle and cluster-count layers. */
function registerClusterLayers(
    mlMap: maplibregl.Map,
    config: {
        sourceId: string;
        clusterLayerId: string;
        countLayerId: string;
        color: string;
        visible: boolean;
    },
): void {
    if (!mlMap.getLayer(config.clusterLayerId)) {
        try {
            addOrbitLayer(
                mlMap,
                {
                    id: config.clusterLayerId,
                    type: 'circle',
                    source: config.sourceId,
                    filter: ['has', 'point_count'],
                    layout: { visibility: config.visible ? 'visible' : 'none' },
                    paint: {
                        'circle-color': config.color,
                        'circle-radius': ['step', ['get', 'point_count'], 16, 10, 20, 100, 24],
                        'circle-stroke-color': '#ffffff',
                        'circle-stroke-width': 2,
                    },
                },
                'poiClusters',
            );
        } catch {
            // Progressive enhancement — layer failures must not break the map.
        }
    }

    if (!mlMap.getLayer(config.countLayerId)) {
        try {
            addOrbitLayer(
                mlMap,
                {
                    id: config.countLayerId,
                    type: 'symbol',
                    source: config.sourceId,
                    filter: ['has', 'point_count'],
                    layout: {
                        visibility: config.visible ? 'visible' : 'none',
                        'text-field': ['get', 'point_count_abbreviated'],
                        // Use a font the OpenFreeMap glyphs endpoint hosts; the MapLibre default
                        // ("Open Sans Regular,Arial Unicode MS Regular") 404s against it.
                        'text-font': ['Noto Sans Regular'],
                        'text-size': 12,
                        'text-allow-overlap': true,
                    },
                    paint: {
                        'text-color': '#ffffff',
                    },
                },
                'poiClusters',
            );
        } catch {
            // Progressive enhancement — layer failures must not break the map.
        }
    }
}

/** Registers a clustered air-valve source and its unclustered icon layer. */
function registerValveSourceAndIconLayer(
    mlMap: maplibregl.Map,
    config: {
        sourceId: string;
        data: FeatureCollection;
        iconLayerId: string;
        iconImageId: string;
        visible: boolean;
    },
): boolean {
    try {
        if (mlMap.getSource(config.sourceId)) {
            (mlMap.getSource(config.sourceId) as maplibregl.GeoJSONSource).setData(config.data);
        } else {
            mlMap.addSource(config.sourceId, {
                type: 'geojson',
                data: config.data,
                cluster: true,
                clusterMaxZoom: AIR_VALVE_CLUSTER_MAX_ZOOM,
                clusterRadius: AIR_VALVE_CLUSTER_RADIUS,
            });
        }
    } catch {
        return false;
    }

    if (!mlMap.getLayer(config.iconLayerId)) {
        try {
            addOrbitLayer(
                mlMap,
                {
                    id: config.iconLayerId,
                    type: 'symbol',
                    source: config.sourceId,
                    // The source split already separates categories; this filter hides clustered
                    // points so only individual (unclustered) valves draw icons (matches POIs).
                    filter: ['!', ['has', 'point_count']],
                    minzoom: AIR_VALVE_MIN_ZOOM,
                    layout: {
                        visibility: config.visible ? 'visible' : 'none',
                        'icon-image': config.iconImageId,
                        'icon-anchor': 'bottom',
                        // The tip sits 6px above the 60px canvas bottom; nudge the icon
                        // down so the tip lands on the geographic coordinate (matches POIs).
                        'icon-offset': [0, 6] as [number, number],
                        'icon-size': 1,
                        'icon-allow-overlap': true,
                    },
                },
                'pois',
            );
        } catch {
            // Progressive enhancement — layer failures must not break the map.
        }
    }

    return true;
}

function registerAirValveSourceAndLayers(
    mlMap: maplibregl.Map,
    data: FeatureCollection,
    overlays: FeatureOverlayState,
): void {
    // Normal air valves (highRisk !== true). Toggled via the "Air Valves" Layers row
    // (useMapLayerVisibility owns subsequent visibility for the icon + cluster layers).
    const normalRegistered = registerValveSourceAndIconLayer(mlMap, {
        sourceId: AIR_VALVE_SOURCE_ID,
        data: selectNormalValves(data),
        iconLayerId: ORBIT_LAYER_IDS.airValves,
        iconImageId: AIR_VALVE_IMAGE_ID,
        visible: overlays.airValves,
    });
    if (normalRegistered) {
        registerClusterLayers(mlMap, {
            sourceId: AIR_VALVE_SOURCE_ID,
            clusterLayerId: ORBIT_LAYER_IDS.airValvesClusters,
            countLayerId: ORBIT_LAYER_IDS.airValvesClusterCount,
            color: AIR_VALVE_CLUSTER_COLOR,
            visible: overlays.airValves,
        });
    }

    // High-risk air valves (highRisk === true). Toggled via the "High Risk Air Valves" row.
    const highRiskRegistered = registerValveSourceAndIconLayer(mlMap, {
        sourceId: AIR_VALVE_HIGH_RISK_SOURCE_ID,
        data: selectHighRiskValves(data),
        iconLayerId: ORBIT_LAYER_IDS.airValvesHighRisk,
        iconImageId: HIGH_RISK_AIR_VALVE_IMAGE_ID,
        visible: overlays.highRiskAirValves,
    });
    if (highRiskRegistered) {
        registerClusterLayers(mlMap, {
            sourceId: AIR_VALVE_HIGH_RISK_SOURCE_ID,
            clusterLayerId: ORBIT_LAYER_IDS.airValvesHighRiskClusters,
            countLayerId: ORBIT_LAYER_IDS.airValvesHighRiskClusterCount,
            color: HIGH_RISK_AIR_VALVE_CLUSTER_COLOR,
            visible: overlays.highRiskAirValves,
        });
    }
}

/**
 * Loads air-valve GeoJSON for the selected client and current viewport and renders it as
 * two independently-clustered categories. The single API response is split by the
 * `highRisk` property into two clustered MapLibre sources (normal / high-risk), each with
 * a cluster-circle layer, a cluster-count layer and an unclustered icon layer. Clicking a
 * cluster ease-zooms to expand it. Sources and layers re-register after every base map
 * style change; data reloads on moveend.
 *
 * Only active in SAVI mode (`enabled`): when disabled it clears both sources and skips all
 * fetching/registration, so the Find & Fix map is never touched by air-valve layers.
 *
 * Safe when no client is selected, below min zoom, on style reloads, and when API
 * requests fail (handled by the shared viewport loader).
 */
export function useAirValvesLayer(
    mapRef: RefObject<MapRef | null>,
    isMapLoaded: boolean,
    selectedClient: Client | null,
    featureOverlays: FeatureOverlayState,
    enabled: boolean,
    // False while a map tool (radial measure) owns the map: cluster click-to-zoom and the
    // cluster pointer cursor stand down, while layers and data stay registered.
    interactionsEnabled: boolean = true,
): void {
    const airValvesDataRef = useRef<FeatureCollection>(EMPTY_FEATURE_COLLECTION);

    // Keep a ref so the registration effect reads current overlay state without re-running
    // when featureOverlays changes (useMapLayerVisibility owns subsequent visibility toggling).
    const overlaysRef = useRef(featureOverlays);
    useLayoutEffect(() => {
        overlaysRef.current = featureOverlays;
    });

    // Load air-valve data for the current viewport when client/map-ready/enabled changes.
    // One fetch feeds both clustered sources: the normal subset via transformFn (primary
    // source) and the high-risk subset via onData (secondary source).
    useEffect(() => {
        airValvesDataRef.current = EMPTY_FEATURE_COLLECTION;
        const mlMap = mapRef.current?.getMap();
        if (mlMap) {
            setGeoJsonSourceData(mlMap, AIR_VALVE_SOURCE_ID, EMPTY_FEATURE_COLLECTION);
            setGeoJsonSourceData(mlMap, AIR_VALVE_HIGH_RISK_SOURCE_ID, EMPTY_FEATURE_COLLECTION);
        }

        if (!enabled || !selectedClient || !isMapLoaded) return;

        return attachViewportLoader(mapRef, {
            sourceId: AIR_VALVE_SOURCE_ID,
            minZoom: AIR_VALVE_MIN_ZOOM,
            clientId: selectedClient.id,
            dataRef: airValvesDataRef,
            fetchFn: fetchAirValvesForBounds,
            transformFn: selectNormalValves,
            onData: (map, data) => {
                setGeoJsonSourceData(map, AIR_VALVE_HIGH_RISK_SOURCE_ID, selectHighRiskValves(data));
            },
        });
    }, [selectedClient, isMapLoaded, enabled, mapRef]);

    // Register source, images and layers when ready, and re-register on every style.load
    // (base map switches reload all sources and layers). Reuses already-loaded data — no refetch.
    useEffect(() => {
        if (!enabled || !isMapLoaded) return;
        const mlMap = mapRef.current?.getMap();
        if (!mlMap) return;

        const register = () => {
            registerAirValveSourceAndLayers(mlMap, airValvesDataRef.current, overlaysRef.current);
            void registerAirValveImages(mlMap).catch(() => {
                // Progressive enhancement — missing images fall back to the default icon.
            });
        };

        register();
        mlMap.on('style.load', register);

        // Registration (and its style.load survival) is unconditional; only the pointer
        // cursor and cluster click-to-zoom listeners stand down while a tool is active.
        if (!interactionsEnabled) {
            return () => {
                mlMap.off('style.load', register);
            };
        }

        // Clicking a cluster ease-zooms the map to expand it (mirrors useEntitiesLayer). Each
        // cluster layer reads its own source. Registered here (not inside register()) so
        // style reloads do not stack duplicate handlers.
        const handleClusterClick = (sourceId: string, layerId: string) => (e: maplibregl.MapMouseEvent) => {
            const features = mlMap.queryRenderedFeatures(e.point, { layers: [layerId] });
            if (!features.length) return;
            const clusterId = features[0].properties['cluster_id'] as number | undefined;
            if (clusterId == null) return;
            const src = mlMap.getSource(sourceId) as maplibregl.GeoJSONSource;
            void src
                .getClusterExpansionZoom(clusterId)
                .then((zoom) => {
                    const geometry = features[0].geometry;
                    if (geometry.type !== 'Point') return;
                    mlMap.easeTo({ center: geometry.coordinates as [number, number], zoom });
                })
                .catch(() => {
                    // ignore cluster expansion errors
                });
        };

        const setPointer = () => {
            mlMap.getCanvas().style.cursor = 'pointer';
        };
        const clearPointer = () => {
            mlMap.getCanvas().style.cursor = '';
        };

        const clusterLayers: Array<{ sourceId: string; layerId: string }> = [
            { sourceId: AIR_VALVE_SOURCE_ID, layerId: ORBIT_LAYER_IDS.airValvesClusters },
            { sourceId: AIR_VALVE_HIGH_RISK_SOURCE_ID, layerId: ORBIT_LAYER_IDS.airValvesHighRiskClusters },
        ];
        const clickHandlers = clusterLayers.map(({ sourceId, layerId }) => {
            const handler = handleClusterClick(sourceId, layerId);
            mlMap.on('click', layerId, handler);
            mlMap.on('mousemove', layerId, setPointer);
            mlMap.on('mouseleave', layerId, clearPointer);
            return { layerId, handler };
        });

        return () => {
            mlMap.off('style.load', register);
            for (const { layerId, handler } of clickHandlers) {
                mlMap.off('click', layerId, handler);
                mlMap.off('mousemove', layerId, setPointer);
                mlMap.off('mouseleave', layerId, clearPointer);
            }
        };
    }, [enabled, interactionsEnabled, isMapLoaded, mapRef]);
}
